/**
 * Campaign Overview Tab Component
 * 
 * Displays campaign information in organized cards:
 * - Campaign Summary
 * - Campaign Type
 * - Validity Dates
 * - Description
 * - Funding Information
 * - Linked Promos
 */

import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { Campaign, CampaignType } from '../../../../../api/promos/types';
import '../../CreateCampaign/components/SummaryCardStyles.css';
import '../../CreateCampaign/components/CampaignPreview.css';
import '../CampaignDetail.css';

interface OverviewTabProps {
  campaign: Campaign;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ campaign }) => {
  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };
  
  // Format campaign type
  const formatCampaignType = (type: CampaignType): string => {
    const typeMap: { [key: string]: string } = {
      'FESTIVAL': 'Festival',
      'SEASONAL': 'Seasonal',
      'PERFORMANCE': 'Performance',
      'PARTNER': 'Partner',
      'INTERNAL': 'Internal',
    };
    return typeMap[type] || type;
  };
  
  // Format funding
  const formatFunding = () => {
    if (!campaign.funding) return 'Platform';
    if (campaign.funding.fundedBy === 'PLATFORM') {
      return 'Platform';
    } else if (campaign.funding.fundedBy === 'SHARED') {
      return `Shared (${campaign.funding.platformShare || 100}%)`;
    }
    return 'Platform';
  };
  
  return (
    <Row className="promo-detail-page">
      {/* Campaign Summary Card */}
      <Col md={4} lg={4} className="mb-4">
        <div className="summary-card">
          <h6 className="summary-card-title">Campaign Summary</h6>
          <div className="campaign-preview-item">
            <span className="campaign-preview-label">Campaign Name</span>
            <div className="campaign-preview-content multi-line">
              {campaign.name || '-'}
            </div>
          </div>
          <div className="campaign-preview-item">
            <span className="campaign-preview-label">Campaign Type</span>
            <div className="campaign-preview-content">
              {campaign.campaignType ? formatCampaignType(campaign.campaignType) : '-'}
            </div>
          </div>
          <div className="campaign-preview-item">
            <span className="campaign-preview-label">Status</span>
            <div className="campaign-preview-content">
              {campaign.status || '-'}
            </div>
          </div>
        </div>
      </Col>
      
      {/* Validity Dates Card */}
      <Col md={4} lg={4} className="mb-4">
        <div className="summary-card">
          <h6 className="summary-card-title">Validity</h6>
          <div className="campaign-preview-item">
            <span className="campaign-preview-label">Start Date</span>
            <div className="campaign-preview-content">
              {formatDate(campaign.startAt)}
            </div>
          </div>
          <div className="campaign-preview-item">
            <span className="campaign-preview-label">End Date</span>
            <div className="campaign-preview-content">
              {formatDate(campaign.endAt)}
            </div>
          </div>
        </div>
      </Col>
      
      {/* Description Card */}
      {campaign.description && (
        <Col md={4} lg={4} className="mb-4">
          <div className="summary-card">
            <h6 className="summary-card-title">Description</h6>
            <div className="campaign-preview-item">
              <span className="campaign-preview-label">Description</span>
              <div className="campaign-preview-content multi-line">
                {campaign.description}
              </div>
            </div>
          </div>
        </Col>
      )}
      
      {/* Funding Information Card */}
      <Col md={4} lg={4} className="mb-4">
        <div className="summary-card">
          <h6 className="summary-card-title">Funding</h6>
          <div className="campaign-preview-item">
            <span className="campaign-preview-label">Funded By</span>
            <div className="campaign-preview-content">
              {formatFunding()}
            </div>
          </div>
          {campaign.funding?.platformShare !== undefined && (
            <div className="campaign-preview-item">
              <span className="campaign-preview-label">Platform Share</span>
              <div className="campaign-preview-content">
                {campaign.funding.platformShare}%
              </div>
            </div>
          )}
        </div>
      </Col>
      
      {/* Linked Promos Card */}
      <Col md={4} lg={4} className="mb-4">
        <div className="summary-card">
          <h6 className="summary-card-title">Linked Promos</h6>
          <div className="campaign-preview-item">
            <span className="campaign-preview-label">Total Linked Promos</span>
            <div className="campaign-preview-content">
              {campaign.linkedPromos?.length || 0}
            </div>
          </div>
          <div className="campaign-preview-item">
            <span className="campaign-preview-label">Active Promos</span>
            <div className="campaign-preview-content">
              {campaign.activePromos || 0}
            </div>
          </div>
        </div>
      </Col>
      
      {/* Metrics Card */}
      <Col md={4} lg={4} className="mb-4">
        <div className="summary-card">
          <h6 className="summary-card-title">Metrics</h6>
          <div className="campaign-preview-item">
            <span className="campaign-preview-label">Total Usage</span>
            <div className="campaign-preview-content">
              {campaign.totalUsage ? campaign.totalUsage.toLocaleString() : '0'}
            </div>
          </div>
          <div className="campaign-preview-item">
            <span className="campaign-preview-label">Revenue Impact</span>
            <div className="campaign-preview-content">
              {campaign.revenueImpact ? `₹${campaign.revenueImpact.toLocaleString('en-IN')}` : '₹0'}
            </div>
          </div>
          {campaign.revenueUplift && (
            <div className="campaign-preview-item">
              <span className="campaign-preview-label">Revenue Uplift</span>
              <div className="campaign-preview-content">
                {campaign.revenueUplift}
              </div>
            </div>
          )}
        </div>
      </Col>
    </Row>
  );
};

export default OverviewTab;
